// Home v2 — responsive app shell. Reusable chrome for any screen.
//  - Desktop: sticky Sidebar + a full-height content area. The content itself
//    owns scrolling so the right column can stay fixed while the middle scrolls.
//  - Tablet (stacked): Sidebar + one ScrollView (whole content scrolls).
//  - Mobile: ScrollView only; the bottom tab bar is global (MobileTabBar).
import React from 'react'
import { ScrollView, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import useTheme from '@common/hooks/useTheme'
import { Mode } from '@web/contexts/appModeContext'

import { HomeLayout } from '../useHomeBreakpoints'
import { NavItem as NavItemType, NavKey } from '../navConfig'
import { LAYOUT } from '../tokens'
import Sidebar from './Sidebar'

type Props = {
  active: NavKey
  mode: Mode
  layout: HomeLayout
  onNavigate: (item: NavItemType) => void
  /**
   * Whether the screen is actually rendering the desktop two-column grid (see
   * `getFitsTwoColumn`). Only that layout owns its own scrolling; a desktop
   * viewport too narrow for the grid falls back to the single-column ScrollView
   * below, sidebar included. Defaults to true for screens with no grid concept.
   */
  twoColumn?: boolean
  children: React.ReactNode
}

const AppShell: React.FC<Props> = ({
  active,
  mode,
  layout,
  onNavigate,
  twoColumn = true,
  children
}) => {
  const { theme } = useTheme()
  // iOS home-screen PWA (standalone) reports a top notch/Dynamic-Island inset
  // and a bottom home-indicator inset. In a normal browser tab these are 0, so
  // adding them is a no-op there. react-native-safe-area-context reads them from
  // env(safe-area-inset-*) on web (needs viewport-fit=cover, already set).
  const insets = useSafeAreaInsets()
  const { isMobile, isStacked, isDesktop } = layout
  const bg = theme.homePageBackground as string

  const sidebar = <Sidebar active={active} mode={mode} onNavigate={onNavigate} />

  // Desktop: no page-level scroll. The screen renders its own left/right scroll
  // model so the right column stays put while the middle scrolls.
  if (isDesktop && twoColumn) {
    return (
      <View style={{ flex: 1, flexDirection: 'row', backgroundColor: bg }}>
        {sidebar}
        {/* Landmark: lets a screen reader jump straight past the sidebar to the
            page content (NVDA's D / region keys). There was no landmark and no
            heading anywhere in the app, so browse-mode navigation meant tabbing
            through the whole chrome on every screen. */}
        {/* @ts-ignore-next-line — react-native-web only */}
        <View role="main" style={{ flex: 1 }}>
          {children}
        </View>
      </View>
    )
  }

  const mainPadding = isMobile ? 16 : 20
  const scroll = (
    <ScrollView
      // @ts-ignore-next-line — react-native-web only (see the landmark note above)
      role="main"
      style={{ flex: 1, backgroundColor: bg }}
      contentContainerStyle={{
        paddingHorizontal: mainPadding,
        paddingTop: (isMobile ? 16 : 32) + insets.top,
        paddingBottom: (isMobile ? LAYOUT.bottomTabHeight + 32 : 32) + insets.bottom,
        alignItems: 'center'
      }}
      showsVerticalScrollIndicator={false}
    >
      <View style={{ width: '100%', maxWidth: LAYOUT.gridLeftWidth }}>{children}</View>
    </ScrollView>
  )

  // Tablet, or a desktop viewport too narrow for the two-column grid: sidebar +
  // one centered ScrollView.
  if (isStacked || isDesktop) {
    return (
      <View style={{ flex: 1, flexDirection: 'row', backgroundColor: bg }}>
        {sidebar}
        {scroll}
      </View>
    )
  }

  // mobile — the bottom tab bar is rendered globally in Router.web (MobileTabBar)
  // so it persists across tab navigations instead of remounting per screen.
  return <View style={{ flex: 1, backgroundColor: bg }}>{scroll}</View>
}

export default React.memo(AppShell)
